import { useCallback, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useLingui } from '@lingui/react/macro';
import { cn } from '@worldcoo/ui/lib/utils';
import { Carousel, CarouselItem } from '@worldcoo/ui/components/carousel';
import { DiamondGrid, diamondGlows } from '../primitives';
import { DigitRoll } from '../digit-roll';
import { useDragScroll } from '../use-drag-scroll';
import { useMediaQuery } from '@worldcoo/ui/hooks/use-media-query';
import { useMilestones } from './milestones-data';
import { MilestoneCard } from './milestone-card';

// Slot geometry lives in the classes, per breakpoint: docs/notes/portals/milestones.md

/** `leading-none` in a cell of the original 1.3 line box keeps the numeral in place and all ten cells equal. */
const YEAR_CELL =
	'flex h-[6.5rem] items-center justify-center text-[5rem] leading-none font-medium tracking-[-0.02em] xl:h-[13.65rem] xl:text-[10.5rem]';

/** Measured, not multiplied: a group is one slide wide or several. */
function groupOffset(el: HTMLUListElement, group: number[]) {
	const slides = group.map((i) => el.children.item(i)).filter((child) => child instanceof HTMLElement);
	const first = slides[0];
	const last = slides[slides.length - 1];
	if (!first || !last) return null;
	const track = el.getBoundingClientRect();
	return (first.getBoundingClientRect().left + last.getBoundingClientRect().right) / 2 - (track.left + track.right) / 2;
}

/** The `xl` slide widths in the classes below: one milestone, or each card of a year that has several. */
const SLIDE_PX = 843;
const PAIRED_SLIDE_PX = 474;
/** The export's side cards: centred 774.5px out, so 98px of each shows at 1440 whatever the years hold. */
const NEIGHBOUR_CENTRE_PX = 774.5;

const slideWidth = (count: number) => (count > 1 ? PAIRED_SLIDE_PX : SLIDE_PX);

/**
 * How far a card is drawn towards the active year, at `xl`. The slides around a two-card year are a
 * different distance away, so one fixed pull left that year's neighbours unevenly exposed (TEC-4010).
 */
function pullTowardActive(years: { count: number }[], activeYear: number, yearIndex: number) {
	const side = Math.sign(yearIndex - activeYear);
	const active = years[activeYear];
	const adjacent = years[activeYear + side];
	if (side === 0 || !active || !adjacent) return 0;
	const centreDistance = (active.count * slideWidth(active.count)) / 2 + slideWidth(adjacent.count) / 2;
	return -side * (centreDistance - NEIGHBOUR_CENTRE_PX);
}

function YearMarker() {
	return (
		<span aria-hidden className="bg-brand relative block size-2 rounded-full">
			<span className="absolute top-1/2 left-1/2 size-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white" />
		</span>
	);
}

/**
 * A native scroll-snap track whose scroll position is the state.
 *
 * How it is laid out and driven: docs/notes/portals/milestones.md
 */
export function Milestones() {
	const { t } = useLingui();
	const milestones = useMilestones();
	// Opens on the first year: a timeline reads from its start.
	const [active, setActive] = useState(0);
	const track = useRef<HTMLUListElement>(null);
	const current = milestones[active];

	// A year axis, not one entry per slide.
	const years = milestones.reduce<{ year: string; first: number; count: number }[]>((acc, milestone, i) => {
		const entry = acc.find((candidate) => candidate.year === milestone.year);
		if (entry) entry.count += 1;
		else acc.push({ year: milestone.year, first: i, count: 1 });
		return acc;
	}, []);
	const activeYear = years.findIndex((entry) => entry.year === current?.year);

	// At `xl` a two-card year is one stop; `matchMedia` because the other half of the pairing is CSS.
	const paired = useMediaQuery('(min-width: 1280px)');
	const yearOf = milestones.map((milestone) => milestone.year).join(',');
	const snapGroups = useMemo(() => {
		const list = yearOf.split(',');
		if (!paired) return list.map((_, i) => [i]);
		return list.reduce<number[][]>((acc, year, i) => {
			const group = acc[acc.length - 1];
			if (group && list[i - 1] === year) group.push(i);
			else acc.push([i]);
			return acc;
		}, []);
	}, [paired, yearOf]);

	// Jumps, never walks year by year: a fast scroll glides past intermediate snap points (2018 vanished), and
	// re-issuing `scrollTo` mid-glide restarts the ease. `behavior` unset defers to the reduced-motion-aware CSS.
	const showMilestone = useCallback(
		(index: number) => {
			const el = track.current;
			if (!el) return;
			const group = snapGroups.find((candidate) => candidate.includes(index));
			const offset = group && groupOffset(el, group);
			if (offset !== undefined && offset !== null) el.scrollTo({ left: el.scrollLeft + offset });
		},
		[snapGroups],
	);

	// The nearest group, not `scrollLeft / step`: slides differ in width. Its first slide, so a paired year reads as one place.
	const nearestStop = useCallback(() => {
		const el = track.current;
		if (!el) return null;
		let nearest = 0;
		let shortest = Infinity;
		for (const group of snapGroups) {
			const offset = groupOffset(el, group);
			if (offset === null || Math.abs(offset) >= shortest) continue;
			shortest = Math.abs(offset);
			nearest = group[0] ?? 0;
		}
		return nearest;
	}, [snapGroups]);

	const onScroll = () => {
		const nearest = nearestStop();
		if (nearest !== null) setActive(nearest);
	};

	/** `scroll-snap-type` does not re-snap after a drag on its own. */
	const settleDrag = useCallback(() => {
		const nearest = nearestStop();
		if (nearest !== null) showMilestone(nearest);
	}, [nearestStop, showMilestone]);

	const dragProps = useDragScroll<HTMLUListElement>({ onDragEnd: settleDrag });

	return (
		<section className="relative bg-white">
			<div className="flex min-h-screen items-center overflow-hidden">
				{/* The export's glow centre, from the right edge so it stays in the corner at any width. */}
				<DiamondGrid
					color="var(--color-brand)"
					opacity={0.95}
					masks={[diamondGlows({ x: 'calc(100% - 50px)', y: '-63px' })]}
				/>

				{/* No side padding below `xl`: the mobile hand-off insets each block differently. */}
				<div className="relative mx-auto flex w-full max-w-[1440px] flex-col gap-10 py-16 md:py-20 xl:h-[738px] xl:gap-0 xl:py-0">
					{/* 36px mobile, measured scale-free off a capture of unknown zoom; every section heading uses the pair. */}
					<h2 className="text-ink ml-[16%] text-4xl leading-[1.3] font-medium tracking-[-0.02em] md:text-[2.5rem] xl:absolute xl:top-[65px] xl:ml-0 xl:w-full xl:text-center">
						{t`Our milestones`}
					</h2>

					{/* Breaks out of the band (`w-screen`) because `.carousel` is the clipping box; the rail is the control. */}
					<Carousel
						ref={track}
						{...dragProps}
						onScroll={onScroll}
						aria-label={t`Our milestones`}
						className="carousel-no-buttons gap-8 px-0 select-none xl:absolute xl:top-[182px] xl:left-1/2 xl:w-screen xl:-translate-x-1/2 xl:gap-0"
					>
						{milestones.map((milestone, i) => {
							const yearIndex = years.findIndex((entry) => entry.year === milestone.year);
							const group = years[yearIndex];
							const count = group?.count ?? 1;
							return (
								<CarouselItem
									key={milestone.id}
									// Spent by `carousel-group-lead` in globals.css, at `xl` only.
									style={{ '--carousel-group-extra': `${(count - 1) * PAIRED_SLIDE_PX}px` } as CSSProperties}
									// `snap-always` stops a fling past a stop without constraining a programmatic scroll.
									className={cn(
										'flex w-[69%] snap-always justify-center first:ml-[15.5%] last:mr-[15.5%] xl:first:ml-[calc(50%-421.5px)] xl:last:mr-[calc(50%-421.5px)]',
										count > 1 ? 'xl:w-[474px]' : 'xl:w-[843px]',
										// The `xl` pairing in globals.css: the first slide carries the group's snap area.
										count > 1 && (i === group?.first ? 'carousel-group-lead' : 'carousel-group-follow'),
									)}
								>
									<MilestoneCard
										milestone={milestone}
										isFocused={i === active}
										inFocusedYear={milestone.year === current?.year}
										pull={pullTowardActive(years, activeYear, yearIndex)}
										onSelect={() => showMilestone(i)}
									/>
								</CarouselItem>
							);
						})}
					</Carousel>

					{/* Rolled, not swapped, so it says time passing. It restates the rail's year, hence `aria-hidden`. */}
					<div aria-hidden className="text-brand ml-5 flex xl:absolute xl:top-[539px] xl:left-8 xl:ml-0">
						{/* Keyed by position, so each column stays one element and rolls on instead of restarting. */}
						{(current?.year ?? '').split('').map((digit, i) => (
							// oxlint-disable-next-line react/no-array-index-key
							<DigitRoll key={i} digit={Number(digit)} cell={YEAR_CELL} />
						))}
					</div>

					{/* A grid, so the marker sits in the active year's column with no measuring. */}
					<div
						// Not scaled down for mobile: its 326px is exactly what a 375px screen leaves inside these gutters.
						className="ml-9 grid gap-x-3 self-start xl:absolute xl:right-8 xl:bottom-8 xl:ml-0 xl:self-auto"
						style={{ gridTemplateColumns: `repeat(${years.length}, auto)` }}
					>
						{years.map(({ year, first }, i) => (
							<button
								key={year}
								type="button"
								onClick={() => showMilestone(first)}
								aria-current={i === activeYear ? 'true' : undefined}
								style={{ gridRow: 1, gridColumn: i + 1 }}
								className={cn(
									'text-ink cursor-pointer text-base leading-[1.3] font-medium tracking-[-0.02em] transition-opacity',
									i === activeYear ? 'opacity-100' : 'opacity-50 hover:opacity-75',
								)}
							>
								{year}
							</button>
						))}
						<span
							aria-hidden
							style={{ gridRow: 2, gridColumn: '1 / -1' }}
							className="bg-brand/30 mt-[9px] h-px self-center"
						/>
						<span
							aria-hidden
							style={{ gridRow: 2, gridColumn: activeYear + 1 }}
							className="mt-[9px] self-center justify-self-center"
						>
							<YearMarker />
						</span>
					</div>
				</div>
			</div>
		</section>
	);
}
